import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X, Building2, Users, FileText, CheckSquare, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  Drawer,
  DrawerContent,
} from "@/components/ui/drawer";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { triggerHaptic } from "@/hooks/useHaptic";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/AuthProvider";
import { cn } from "@/lib/utils";

interface MobileSearchDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface SearchResult {
  id: string;
  type: 'project' | 'client' | 'proposal' | 'task';
  title: string;
  subtitle?: string;
  href: string;
}

const typeConfig = {
  project: { icon: Building2, label: "Projeto", color: "text-primary" },
  client: { icon: Users, label: "Cliente", color: "text-blue-500" },
  proposal: { icon: FileText, label: "Proposta", color: "text-emerald-500" },
  task: { icon: CheckSquare, label: "Tarefa", color: "text-amber-500" },
};

export function MobileSearchDrawer({ open, onOpenChange }: MobileSearchDrawerProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const navigate = useNavigate();
  const { user } = useAuth();

  // Load recent searches from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('obrim-recent-searches');
    if (saved) {
      try {
        setRecentSearches(JSON.parse(saved).slice(0, 5));
      } catch {
        // Invalid JSON, ignore
      }
    }
  }, []);

  // Save search to recent
  const saveRecentSearch = (term: string) => {
    const updated = [term, ...recentSearches.filter(s => s !== term)].slice(0, 5);
    setRecentSearches(updated);
    localStorage.setItem('obrim-recent-searches', JSON.stringify(updated));
  };

  // Search function with debounce
  const performSearch = useCallback(async (searchQuery: string) => {
    if (!searchQuery.trim() || !user) {
      setResults([]);
      return;
    }

    setIsLoading(true);
    const searchResults: SearchResult[] = [];

    try {
      // Search projects
      const { data: projects } = await supabase
        .from('projects')
        .select('id, name, client')
        .eq('user_id', user.id)
        .ilike('name', `%${searchQuery}%`)
        .limit(5);

      if (projects) {
        projects.forEach(p => {
          searchResults.push({
            id: p.id,
            type: 'project',
            title: p.name,
            subtitle: p.client,
            href: `/projeto/${p.id}`
          });
        });
      }

      // Search clients
      const { data: clients } = await supabase
        .from('clientes')
        .select('id, nome_completo, email')
        .eq('user_id', user.id)
        .ilike('nome_completo', `%${searchQuery}%`)
        .limit(5);

      if (clients) {
        clients.forEach(c => {
          searchResults.push({
            id: c.id,
            type: 'client',
            title: c.nome_completo,
            subtitle: c.email,
            href: `/clientes/${c.id}`
          });
        });
      }

      // Search proposals
      const { data: proposals } = await supabase
        .from('propostas')
        .select('id, numero, cliente_nome')
        .eq('user_id', user.id)
        .or(`numero.ilike.%${searchQuery}%,cliente_nome.ilike.%${searchQuery}%`)
        .limit(5);

      if (proposals) {
        proposals.forEach(p => {
          searchResults.push({
            id: p.id,
            type: 'proposal',
            title: `Proposta ${p.numero}`,
            subtitle: p.cliente_nome,
            href: `/propostas?view=${p.id}`
          });
        });
      }

      // Search tasks
      const { data: tasks } = await supabase
        .from('project_tasks')
        .select('id, name, project_id')
        .eq('user_id', user.id)
        .ilike('name', `%${searchQuery}%`)
        .limit(5);

      if (tasks) {
        tasks.forEach(t => {
          searchResults.push({
            id: t.id,
            type: 'task',
            title: t.name,
            href: `/projeto/${t.project_id}/cronograma`
          });
        });
      }

      setResults(searchResults);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  // Debounced search
  useEffect(() => {
    const timeout = setTimeout(() => {
      performSearch(query);
    }, 300);

    return () => clearTimeout(timeout);
  }, [query, performSearch]);

  // Clear on close
  useEffect(() => {
    if (!open) {
      setQuery("");
      setResults([]);
    }
  }, [open]);

  const handleResultClick = (result: SearchResult) => {
    triggerHaptic('light');
    saveRecentSearch(result.title);
    onOpenChange(false);
    setTimeout(() => {
      navigate(result.href);
    }, 150);
  };

  const handleRecentClick = (term: string) => {
    triggerHaptic('light');
    setQuery(term);
  };

  const clearRecentSearches = () => {
    triggerHaptic('light');
    setRecentSearches([]);
    localStorage.removeItem('obrim-recent-searches');
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[90vh]">
        {/* Search Input */}
        <div className="p-4 border-b border-border/50">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              autoFocus
              placeholder="Buscar projetos, clientes, tarefas..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-10 pr-10 h-12 rounded-xl bg-muted/50 border-0 text-base"
            />
            {query && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8"
                onClick={() => setQuery("")}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        <div className="overflow-y-auto max-h-[60vh] pb-8">
          {/* Recent Searches */}
          {!query && recentSearches.length > 0 && (
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Buscas Recentes
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 text-xs text-muted-foreground"
                  onClick={clearRecentSearches}
                >
                  Limpar
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {recentSearches.map((term, i) => (
                  <button
                    key={i}
                    onClick={() => handleRecentClick(term)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-muted/60 text-sm text-muted-foreground hover:bg-muted transition-colors"
                  >
                    <Clock className="h-3 w-3" />
                    {term}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Loading */}
          {isLoading && (
            <div className="p-8 text-center">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          )}

          {/* Results */}
          {!isLoading && results.length > 0 && (
            <div className="p-2">
              <AnimatePresence>
                {results.map((result, index) => {
                  const config = typeConfig[result.type];
                  const Icon = config.icon;
                  
                  return (
                    <motion.button
                      key={result.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0, transition: { delay: index * 0.03 } }}
                      exit={{ opacity: 0, y: -10 }}
                      onClick={() => handleResultClick(result)}
                      className={cn(
                        "w-full flex items-center gap-3 p-3 rounded-xl",
                        "text-left transition-colors",
                        "hover:bg-muted/60 active:bg-muted"
                      )}
                    >
                      <div className={cn(
                        "w-10 h-10 rounded-lg flex items-center justify-center",
                        "bg-muted/60"
                      )}>
                        <Icon className={cn("h-5 w-5", config.color)} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{result.title}</p>
                        {result.subtitle && (
                          <p className="text-xs text-muted-foreground truncate">
                            {result.subtitle}
                          </p>
                        )}
                      </div>
                      <span className="text-[10px] text-muted-foreground bg-muted/60 px-2 py-0.5 rounded-full">
                        {config.label}
                      </span>
                    </motion.button>
                  );
                })}
              </AnimatePresence>
            </div>
          )}

          {/* No Results */}
          {!isLoading && query && results.length === 0 && (
            <div className="p-8 text-center">
              <Search className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">
                Nenhum resultado para "{query}"
              </p>
            </div>
          )}

          {/* Empty State */}
          {!query && recentSearches.length === 0 && (
            <div className="p-8 text-center">
              <Search className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">
                Digite para buscar projetos, clientes e mais
              </p>
            </div>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
